import React from "react";
import {
  Package,
  ScanLine,
  MapPin,
  Truck,
  AlertTriangle,
  StickyNote,
  CheckCircle2,
  User,
  Weight,
  Send,
  Clock,
  Loader2,
  PackageX,
} from "lucide-react";

const EVENT_META = {
  scan: { Icon: ScanLine, color: "text-blue-600", ring: "border-blue-200 bg-blue-50", label: "Scan" },
  checkpoint: { Icon: MapPin, color: "text-emerald-600", ring: "border-emerald-200 bg-emerald-50", label: "Checkpoint" },
  out_for_delivery: { Icon: Truck, color: "text-amber-600", ring: "border-amber-200 bg-amber-50", label: "Out for delivery" },
  exception: { Icon: AlertTriangle, color: "text-red-600", ring: "border-red-200 bg-red-50", label: "Exception" },
  note: { Icon: StickyNote, color: "text-zinc-600", ring: "border-zinc-200 bg-zinc-50", label: "Note" },
};

function statusStyle(status = "") {
  const s = status.toLowerCase();
  if (s.includes("deliver")) return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (s.includes("transit") || s.includes("out")) return "bg-amber-100 text-amber-700 border-amber-200";
  if (s.includes("exception") || s.includes("hold")) return "bg-red-100 text-red-700 border-red-200";
  return "bg-zinc-100 text-zinc-700 border-zinc-200";
}

function fmtDate(d, withTime = false) {
  if (!d) return "\u2014";
  try {
    const dt = new Date(d);
    return withTime ? dt.toLocaleString() : dt.toLocaleDateString();
  } catch {
    return "\u2014";
  }
}

function buildTrail(data) {
  const trail = [];
  if (data.created_at) {
    trail.push({
      id: "__shipped",
      Icon: Package,
      color: "text-zinc-600",
      ring: "border-zinc-200 bg-zinc-100",
      label: "Shipped",
      branch_name: null,
      notes: "Package created in courier system",
      created_at: data.created_at,
    });
  }
  (data.events || []).forEach((e) => {
    const meta = EVENT_META[e.event_type] || EVENT_META.scan;
    trail.push({
      id: e.id,
      Icon: meta.Icon,
      color: meta.color,
      ring: meta.ring,
      label: meta.label,
      branch_name: e.branch_name,
      notes: e.notes,
      created_at: e.created_at,
    });
  });
  if ((data.status || "").toLowerCase() === "delivered") {
    trail.push({
      id: "__delivered",
      Icon: CheckCircle2,
      color: "text-emerald-600",
      ring: "border-emerald-200 bg-emerald-50",
      label: "Delivered",
      branch_name: null,
      notes: "Package successfully delivered",
      created_at: data.updated_at,
    });
  }
  return trail;
}

function Detail({ Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400 mb-1">
        <Icon size={13} />
        {label}
      </div>
      <div className="text-[14px] font-medium text-[#1a1a1a]">{value || "\u2014"}</div>
    </div>
  );
}

export default function TrackingResult({ loading, error, data }) {
  if (loading) {
    return (
      <div className="mt-8 rounded-xl border border-gray-200 bg-white shadow-[0_10px_40px_rgba(0,0,0,0.06)] p-8 flex items-center gap-3 text-[#5f5f5f]">
        <Loader2 className="animate-spin text-[#ED2E24]" size={20} />
        <span className="text-[15px]">Fetching package status…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-8 rounded-xl border border-red-200 bg-red-50 p-8 flex items-center gap-3 text-red-700">
        <PackageX size={22} />
        <span className="text-[15px] font-medium">{error}</span>
      </div>
    );
  }

  if (!data) return null;

  const weight = data.weight_kg
    ? `${Number(data.weight_kg).toFixed(2)} kg / ${(2.20462 * Number(data.weight_kg)).toFixed(2)} lb`
    : "\u2014";
  const trail = buildTrail(data);

  return (
    <div className="mt-8 rounded-xl border border-gray-200 bg-white shadow-[0_18px_50px_rgba(0,0,0,0.08)] overflow-hidden">
      {/* header */}
      <div className="px-6 lg:px-8 py-6 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400">Tracking #</div>
          <div className="text-[20px] font-bold text-[#1a1a1a]">{data.tracking_number || "\u2014"}</div>
        </div>
        <span className={`px-4 py-1.5 rounded-full text-[13px] font-semibold border capitalize ${statusStyle(data.status)}`}>
          {data.status || "Unknown"}
        </span>
      </div>

      {/* details */}
      <div className="px-6 lg:px-8 py-6 grid grid-cols-2 lg:grid-cols-4 gap-6 border-b border-gray-100">
        <Detail Icon={User} label="Recipient" value={data.recipient_name} />
        <Detail Icon={Weight} label="Weight" value={weight} />
        <Detail Icon={Send} label="Method" value={data.shipment_method} />
        <Detail Icon={Clock} label="Shipped" value={fmtDate(data.created_at)} />
        {data.recipient_address && (
          <div className="col-span-2 lg:col-span-4">
            <Detail Icon={MapPin} label="Deliver to" value={data.recipient_address} />
          </div>
        )}
      </div>

      {/* hub trail */}
      <div className="px-6 lg:px-8 py-6">
        <div className="flex items-center gap-2 mb-5">
          <MapPin size={15} className="text-[#ED2E24]" />
          <h3 className="text-[15px] font-semibold text-[#1a1a1a]">Journey / Hub trail</h3>
          <span className="text-[12px] text-gray-400">
            {trail.length} {trail.length === 1 ? "event" : "events"}
          </span>
        </div>

        {trail.length === 0 ? (
          <div className="text-[13px] text-gray-500 italic border border-dashed border-gray-200 rounded-md p-6 text-center">
            No scan events yet. Your package journey will appear here.
          </div>
        ) : (
          <ol className="relative">
            {trail.map((ev, idx) => (
              <li key={ev.id || idx} className="flex gap-4 pb-6 last:pb-0 relative">
                {idx < trail.length - 1 && (
                  <span className="absolute left-[19px] top-10 bottom-0 w-px bg-gray-200" aria-hidden="true" />
                )}
                <span className={`shrink-0 w-10 h-10 rounded-full border flex items-center justify-center ${ev.ring}`}>
                  <ev.Icon size={18} className={ev.color} />
                </span>
                <div className="pt-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[14px] font-semibold text-[#1a1a1a]">{ev.label}</span>
                    {ev.branch_name && (
                      <span className="text-[12px] text-gray-500">· {ev.branch_name}</span>
                    )}
                  </div>
                  {ev.notes && <p className="text-[13px] text-[#5f5f5f] mt-0.5">{ev.notes}</p>}
                  <p className="text-[12px] text-gray-400 mt-1">{fmtDate(ev.created_at, true)}</p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
